/**
 * DelayedSpinner — `active`-driven wrapper that:
 *  - waits `delayMs` before mounting children (so quick loads never flash),
 *  - once shown, keeps children mounted for at least `minShowMs` (so the
 *    spinner doesn't blink when a load barely crosses the delay threshold).
 *
 *   <DelayedSpinner active={isLoading}>
 *     <OperatorSpinner label="Loading conversation…" />
 *   </DelayedSpinner>
 */

import { useEffect, useRef, useState } from 'react';

export interface DelayedSpinnerProps {
  /** Whether a load is currently in progress. */
  active: boolean;
  /** Wait this long after `active` flips true before showing the spinner. */
  delayMs?: number;
  /** Once visible, keep showing for at least this long after `active` flips false. */
  minShowMs?: number;
  children: React.ReactNode;
}

export function DelayedSpinner({
  active,
  delayMs = 250,
  minShowMs = 400,
  children,
}: DelayedSpinnerProps) {
  const [visible, setVisible] = useState(false);
  const showTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (active) {
      // Cancel any pending hide so a re-activated load keeps the spinner up.
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (visible || showTimerRef.current !== null) return;
      showTimerRef.current = window.setTimeout(() => {
        showTimerRef.current = null;
        shownAtRef.current = Date.now();
        setVisible(true);
      }, delayMs);
      return;
    }

    // active is false
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (!visible) return;

    const elapsed = Date.now() - (shownAtRef.current ?? Date.now());
    const remaining = minShowMs - elapsed;
    if (remaining <= 0) {
      shownAtRef.current = null;
      setVisible(false);
    } else if (hideTimerRef.current === null) {
      hideTimerRef.current = window.setTimeout(() => {
        hideTimerRef.current = null;
        shownAtRef.current = null;
        setVisible(false);
      }, remaining);
    }
  }, [active, delayMs, minShowMs, visible]);

  useEffect(() => {
    return () => {
      if (showTimerRef.current !== null) window.clearTimeout(showTimerRef.current);
      if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  if (!visible) return null;
  return <>{children}</>;
}

export default DelayedSpinner;
